import React, {useState, useEffect, useRef} from "react";
import { phoneNumber } from "../../../../util/formatter.ts";
import {validatePhone, validateEmail, stripPhonePrefix, scrollToElement} from "../../../../util/helpers.ts"
import {FlowData, RegisterFunction} from "../../../../util/types.ts";
import {ScrollToAnchorPoint} from "../../../../util/ScrollToAnchor.tsx";
import Icon from "../../Icon.tsx";
import IconArrow from "../../../icons/IconArrow.tsx";
import IconEdit from "../../../icons/IconEdit.tsx";

interface ContactInformationComponentProps {
    flowData: FlowData;
    setFlowData: React.Dispatch<React.SetStateAction<FlowData>>;
    register: RegisterFunction;
}

interface ContactInputProps {
    id: string;
    label: string;
    value: string;
    type: "email" | "tel";
    isChecked: boolean;
    editEnabled: boolean;
    onToggleChecked: () => void;
    onToggleEdit: () => void;
    onValueChange: (newValue: string) => void;
    error: string;
}

export default function ContactInformationComponent({
    flowData,
    setFlowData,
    register,
}: ContactInformationComponentProps) {
    const [isEmailChecked, setIsEmailChecked] = useState(false);
    const [isPhoneChecked, setIsPhoneChecked] = useState(false);
    const [editEmailEnabled, setEditEmailEnabled] = useState(false);
    const [editPhoneEnabled, setEditPhoneEnabled] = useState(false);

    useEffect(() => {
        setFlowData(prevFlowData => ({
            ...prevFlowData,
            selectedContact: { email: isEmailChecked, phone: isPhoneChecked },
            editingContact: { email: editEmailEnabled, phone: editPhoneEnabled }
        }));
    }, [isEmailChecked, isPhoneChecked, editEmailEnabled, editPhoneEnabled, setFlowData]);

    const emailError = React.useMemo(() =>
            validateEmail(flowData.email) ? '' : 'Mailadressen er ugyldig. Kontrollér for stavefejl og prøv igen.',
        [flowData.email]
    );

    const phoneError = React.useMemo(() =>
            validatePhone(flowData.phone) ? '' : 'Telefonnummeret er ugyldigt. Kontrollér for stavefejl og prøv igen.',
        [flowData.phone]
    );

    const handlePhoneToggleEdit = () => {
        if (editPhoneEnabled) {
            if (!flowData.phone.startsWith('0045') && !flowData.phone.startsWith('+45')) {
                setFlowData({...flowData, phone: '0045' + flowData.phone});
            }
        }
        setContactError('');
        setEditPhoneEnabled(prev => !prev);
    };

    const [contactError, setContactError] = useState<string>('');
    const hasError = contactError.length > 0;
    const contactInputsComponentTop = useRef() as React.MutableRefObject<HTMLDivElement>;

    useEffect((): void => {
        register("ContactInformationComponent",
            {
                hasErrors: (flowData: FlowData): boolean => {
                    if (flowData.editingContact.phone || flowData.editingContact.email) {
                        setContactError("Gem ændringerne ved at trykke på pilen.");
                        return true
                    } else if (!Object.values(flowData.selectedContact).some((isSelected) => isSelected)) {
                        setContactError("Vælg hvordan vi skal kontakte dig.");
                        return true
                    }

                    if (!validatePhone(flowData.phone) && flowData.selectedContact.phone) {
                        setContactError('Det telefonnummer du har skrevet, er ugyldigt. Kontrollér for tastefejl og prøv igen.');
                        return true
                    }

                    if (!validateEmail(flowData.email) && flowData.selectedContact.email) {
                        setContactError('Den mailadresse du har skrevet, er ugyldig. Kontrollér for stavefejl og prøv igen.');
                        return true
                    }
                    return false
                },
                scrollToError: (): void => {
                    scrollToElement(contactInputsComponentTop.current)
                },
            },
        )
    }, [register, contactError]);

    return (
        <div className={`input-container${hasError ? ' error' : ''}`} ref={contactInputsComponentTop}>
            <ScrollToAnchorPoint id={'contact-inputs'} className={'id-anchor--selfcare-report-error'}/>
            <p>Hvordan skal vi kontakte dig for at følge op på fejlmeldingen?*</p>
            <div className={'contact-row'}>
                <ContactInput
                    id="contact_email"
                    label="Mail"
                    value={flowData.email}
                    type="email"
                    isChecked={isEmailChecked}
                    editEnabled={editEmailEnabled}
                    onToggleChecked={() => { setContactError(''); setIsEmailChecked(prev => !prev); }}
                    onToggleEdit={() => { setContactError(''); setEditEmailEnabled(prev => !prev); }}
                    onValueChange={value => setFlowData({...flowData, email: value})}
                    error={emailError}
                />
                <ContactInput
                    id="contact_phone"
                    label="Telefon"
                    value={editPhoneEnabled ? stripPhonePrefix(flowData.phone) : phoneNumber(stripPhonePrefix(flowData.phone))}
                    type="tel"
                    isChecked={isPhoneChecked}
                    editEnabled={editPhoneEnabled}
                    onToggleChecked={() => { setContactError(''); setIsPhoneChecked(prev => !prev); }}
                    onToggleEdit={handlePhoneToggleEdit}
                    onValueChange={value => setFlowData({...flowData, phone: value})}
                    error={phoneError}
                />
            </div>
            <p className={'small'}>
                Du kan vælge begge kontaktmuligheder, hvis du ikke har nogen præference.
            </p>
            {contactError.length > 0 && <p className={'error-message'}>{contactError}</p>}
        </div>
    );
}

function ContactInput({
    id,
    label,
    value,
    type,
    isChecked,
    editEnabled,
    onToggleChecked,
    onToggleEdit,
    onValueChange,
    error,
}: ContactInputProps) {

    const handleInputOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        onValueChange(e.target.value);
        if (!isChecked) {
            onToggleChecked();
        }
    }

    const handleInputKeyDown = (e: React.KeyboardEvent): void => {
        const supportedKeys = ['Enter', 'Escape'];
        if (supportedKeys.includes(e.key)) {
            e.preventDefault();
            onToggleEdit();
        }
    }

    return (
        <div className={'contact-item'}>
            <p className={'with-checkbox'}>
                <input
                    type="checkbox"
                    id={`${id}_checkbox`}
                    name={`${id}_checkbox`}
                    checked={isChecked}
                    onChange={onToggleChecked}
                />
                <label
                    htmlFor={`${id}_checkbox`}
                    className={'checkbox-label checkbox-label--montserrat'}
                >
                    {label}
                    {!editEnabled && (
                        <small>
                            <span className={'text'}>
                                {value}
                            </span>
                            <button
                                className={'icon-button'}
                                onClick= {e => { e.preventDefault(); onToggleEdit(); }}
                                aria-label={`Redigér ${label.toLowerCase()}`}
                            >
                                <Icon component={IconEdit}/>
                            </button>
                        </small>
                    )}
                </label>
            </p>
            {editEnabled && (
                <div className={'contact-input'}>
                    <input
                        id={`${id}_input`}
                        name={id}
                        value={value}
                        type={type}
                        className={`readonly ${error ? 'error' : ''}`}
                        disabled={!editEnabled}
                        onChange={handleInputOnChange}
                        onKeyDown={handleInputKeyDown}
                    />
                    <button
                        className={'icon-button'}
                        onClick={onToggleEdit}
                        aria-label={`Bekræft ændring af ${label.toLowerCase()}`}
                    >
                        <Icon component={IconArrow}/>
                    </button>
                </div>
            )}
            {error.length > 0 && <p className={'error-message'}>{error}</p>}
        </div>
    );
}
